package com.patronat.top.ui.molecules.inputs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import com.joelkanyi.jcomposecountrycodepicker.annotation.RestrictedApi
import com.joelkanyi.jcomposecountrycodepicker.component.CountryCodePicker
import com.joelkanyi.jcomposecountrycodepicker.component.rememberKomposeCountryCodePickerState
import com.joelkanyi.jcomposecountrycodepicker.data.Country
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft950

enum class AppInputMode {
    Text,
    Phone
}

internal const val DefaultPhoneCountryCode = "ua"

internal data class ResolvedPhoneValue(
    val dialCode: String,
    val localNumber: String
)

@Composable
internal fun rememberPhonePickerState(
    defaultCountryCode: String = DefaultPhoneCountryCode
): CountryCodePicker = rememberKomposeCountryCodePickerState(
    defaultCountryCode = defaultCountryCode.lowercase(),
    showCountryCode = true,
    showCountryFlag = true
)

internal fun resolvePhoneValue(
    rawValue: String,
    countries: List<Country>,
    fallbackCountryCode: String
): ResolvedPhoneValue {
    val normalized = rawValue
        .filter { it.isDigit() || it == '+' }
        .let { if (it.isNotBlank() && !it.startsWith("+")) "+$it" else it }

    val fallbackCountry = countries.firstOrNull {
        it.code.equals(fallbackCountryCode, ignoreCase = true)
    } ?: countries.firstOrNull()

    if (normalized.isBlank()) {
        return ResolvedPhoneValue(dialCode = fallbackCountry?.phoneNoCode.orEmpty(), localNumber = "")
    }

    val matchedCountry = countries
        .sortedByDescending { it.phoneNoCode.length }
        .firstOrNull { normalized.startsWith(it.phoneNoCode) }
        ?: fallbackCountry

    if (matchedCountry == null) {
        return ResolvedPhoneValue(dialCode = "", localNumber = normalized.filter(Char::isDigit))
    }

    val localNumber = normalized
        .removePrefix(matchedCountry.phoneNoCode)
        .filter(Char::isDigit)

    return ResolvedPhoneValue(dialCode = matchedCountry.phoneNoCode, localNumber = localNumber)
}

internal fun combinePhone(dialCode: String, localNumberRaw: String): String {
    val localDigits = localNumberRaw.filter(Char::isDigit)
    if (localDigits.isEmpty()) return ""
    return if (dialCode.isBlank()) localDigits else dialCode + localDigits
}

@Composable
@OptIn(RestrictedApi::class)
internal fun PhoneCountryCodePicker(
    state: CountryCodePicker,
    rawValue: String,
    enabled: Boolean,
    readOnly: Boolean,
    textColor: Color = MineShaft950,
    textStyle: TextStyle = BodyMedium,
    onDialCodeChanged: (String) -> Unit
) {
    LaunchedEffect(rawValue, state.countryList) {
        val normalized = rawValue
            .filter { it.isDigit() || it == '+' }
            .let { if (it.isNotBlank() && !it.startsWith("+")) "+$it" else it }

        if (normalized.isBlank()) return@LaunchedEffect

        val matchedCountry = state.countryList
            .sortedByDescending { it.phoneNoCode.length }
            .firstOrNull { normalized.startsWith(it.phoneNoCode) }
            ?: return@LaunchedEffect

        if (!matchedCountry.code.equals(state.countryCode, ignoreCase = true)) {
            state.setCode(matchedCountry.code.lowercase())
        }
    }

    val dialCode = state.getCountryPhoneCode()
    LaunchedEffect(dialCode) {
        onDialCodeChanged(dialCode)
    }

    val selectedCountry = state.countryList.firstOrNull {
        it.code.equals(state.countryCode, ignoreCase = true)
    } ?: state.countryList.firstOrNull()
    var isDialogVisible by rememberSaveable { mutableStateOf(false) }

    PhoneCountrySelector(
        country = selectedCountry,
        textStyle = textStyle.copy(color = textColor),
        enabled = enabled && !readOnly,
        onClick = { isDialogVisible = true }
    )

    if (isDialogVisible) {
        PhoneCountrySelectionDialog(
            countries = state.countryList,
            onDismissRequest = { isDialogVisible = false },
            onCountrySelected = { selected ->
                state.setCode(selected.code.lowercase())
                isDialogVisible = false
            }
        )
    }
}
